import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon Temmuz 2026 sosyal medya medya planı.
 * 1 – 31 Temmuz 2026 (31 gün), FB + IG, hedef: Etkileşim / Erişim.
 * Bütçe modeli Canon Beyond The Frame / Nisan 2026 planıyla aynı çatı:
 *   TOPLAM → AHK %10 → Lokalizasyon + Medya Planlama → Stopaj %15 → NET medya.
 * 4 içerik: Owned Post, Owned Story, Carousel, 1 Video (tümü owned).
 * (Partner Post + Partner Story çıkarıldı; payları kalan 4 içeriğe oransal dağıtıldı — her pay ÷0.67.)
 */
export default function CanonJuly2026() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe kırılımı ───────────────────────────────────────────────────────
  const TOTAL = 250000;
  const AHK_RATE = 0.10;
  const AHK_FEE = Math.round(TOTAL * AHK_RATE);   // 25.000 — Ajans Hizmet Komisyonu
  const LOCALIZATION = 35000;                      // Lokalizasyon + Medya Planlama
  const MEDIA_BRUT = TOTAL - AHK_FEE - LOCALIZATION; // 190.000 (stopaj dahil medya)
  const STOPAJ_RATE = 0.15;
  const STOPAJ = Math.round(MEDIA_BRUT * STOPAJ_RATE); // 28.500
  const NET = MEDIA_BRUT - STOPAJ;                 // 161.500 — Meta'da gerçek harcanan

  // ── Süre ──────────────────────────────────────────────────────────────────
  const DAYS = 31; // 1 → 31 Temmuz dahil
  const dailyNet = NET / DAYS;

  // ── Gerçekleşen birim maliyetler (Canon Meta hesabı) ───────────────────────
  // Kaynak: brand_canon_…  meta_campaign_performance_raw, Mayıs–Haziran 2026
  // (Etkileşim/Erişim ağırlıklı dönem). Hesap yerleşim kırılımı vermediği için
  // birim maliyetler blended uygulanır; format satırları bütçe payına göre ayrışır.
  const REAL_CPM = 11.0;        // gerçekleşen blended CPM ₺10.98 → 11
  const REAL_ENG_RATE = 0.104;  // gerçekleşen etkileşim oranı %10.44 (actions/impr)
  const REAL_CPV = 5.5;         // gerçekleşen ThruPlay başına maliyet ₺5.51

  // ── İçerik / format dağılımı ───────────────────────────────────────────────
  const FREQ = 2.5; // 31 günlük tek-ay kampanya için tahmini frekans (tek açık varsayım)
  // Partner Post + Partner Story çıkarıldı; payları kalan 4 owned içeriğe oransal dağıtıldı (her eski pay ÷0.67).
  const formatDefs = [
    { name: 'Owned Post',    size: '1080×1350', placement: 'IG/FB Feed',          share: 0.18 / 0.67 },
    { name: 'Owned Story',   size: '1080×1920', placement: 'IG/FB Stories',       share: 0.15 / 0.67 },
    { name: 'Carousel',      size: '1080×1080', placement: 'IG/FB Feed Carousel', share: 0.14 / 0.67 },
    { name: 'Video (Reels)', size: '1080×1920', placement: 'IG/FB Reels & Feed',  share: 0.20 / 0.67, video: true },
  ];
  let allocated = 0;
  const formats = formatDefs.map((f, i) => {
    const budget = i === formatDefs.length - 1 ? NET - allocated : Math.round(NET * f.share);
    allocated += budget;
    const impressions = Math.round((budget / REAL_CPM) * 1000);
    const reach = Math.round(impressions / FREQ);
    const engagement = Math.round(impressions * REAL_ENG_RATE);
    return { ...f, cpm: REAL_CPM, budget, impressions, reach, engagement };
  });

  const totals = formats.reduce((t, f) => ({
    budget: t.budget + f.budget,
    impressions: t.impressions + f.impressions,
    engagement: t.engagement + f.engagement,
  }), { budget: 0, impressions: 0, engagement: 0 });
  const blendedCpm = (totals.budget / totals.impressions) * 1000;
  const estReach = Math.round(totals.impressions / FREQ);
  const engRate = (totals.engagement / totals.impressions) * 100;

  // Video ThruPlay (tahmini) — video bütçesi üzerinden, gerçekleşen CPV ile
  const CPV = REAL_CPV;
  const videoBudget = formats.find((f) => f.video)?.budget || 0;
  const thruPlay = Math.round(videoBudget / CPV);

  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(slideRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save('Canon_Temmuz_2026_Medya_Plani.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">Temmuz 2026 — Sosyal Medya Medya Planı</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Etkileşim / Erişim · 1 – 31 Temmuz 2026</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya detayı */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon'un Temmuz 2026 sosyal medya kampanyası. <strong>1 – 31 Temmuz 2026</strong> tarihleri arasında
              (31 gün) FB + IG'de yayında. Amaç: <strong>Etkileşim / Erişim</strong> — tümüyle owned içerik karmasıyla
              marka bilinirliğini ve etkileşimini artırmak. 4 içerik (Owned Post,
              Owned Story, Carousel ve 1 adet Video) farklı yerleşimlerde dengeli olarak yayınlanır.
            </p>
          </div>

          {/* Hedefleme */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Hedefleme</p>

            {/* Demografi + kanal kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Hedef" value="Etkileşim / Erişim" sub="marka bilinirliği + etkileşim" tone="red" />
              <Stat label="Yaş / Cinsiyet" value="18–55" sub="Tüm cinsiyetler" />
              <Stat label="Dil" value="Türkçe" sub="TR yerleşik" />
              <Stat label="Platform" value="FB + IG" sub="Meta Ads · otomatik yerleşim" />
            </div>

            {/* Lokasyon */}
            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Lokasyon</span>
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                <strong>Tüm Türkiye</strong> — büyükşehir ağırlıklı (İstanbul, Ankara, İzmir, Bursa, Antalya, Adana)
                üst-orta gelir grubu yoğunluklu yerleşimler. Erişim hedefinde ülke geneli, etkileşim/retargeting
                setlerinde metropol ağırlığı.
              </p>
            </div>

            {/* İlgi alanları */}
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">İlgi Alanları (Detaylı Hedefleme)</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['Fotoğrafçılık', 'Dijital fotoğraf makineleri', 'Kamera & ekipman', 'Aynasız fotoğraf makineleri',
                  'Videografi', 'Seyahat fotoğrafçılığı', 'İçerik üreticiliği', 'Vlog & YouTube', 'Sosyal medya içerik üreticileri',
                  'Doğa & açık hava', 'Sanat & tasarım', 'Drone & gimbal', 'Yeni teknoloji meraklıları', 'Düğün & etkinlik fotoğrafçılığı'].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">{t}</span>
                ))}
              </div>
            </div>

            {/* Davranışlar */}
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Davranışlar</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['Etkileşimli alışveriş yapanlar', 'Teknoloji erken benimseyenler', 'Sık seyahat edenler', 'Premium marka ilgisi'].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{t}</span>
                ))}
              </div>
            </div>

            {/* Özel & Benzer kitleler */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                <span className="text-[10px] font-medium uppercase text-emerald-700">Özel Kitleler (Custom Audience)</span>
                <ul className="text-[11px] text-gray-700 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
                  <li>Son 365 gün IG/FB sayfa & post etkileşimcileri</li>
                  <li>Video izleyiciler (%25+ izleme)</li>
                  <li>Web sitesi ziyaretçileri (Pixel, son 180 gün)</li>
                </ul>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                <span className="text-[10px] font-medium uppercase text-emerald-700">Benzer Kitleler (Lookalike)</span>
                <ul className="text-[11px] text-gray-700 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
                  <li>Etkileşimciler LAL %1–3 (TR)</li>
                  <li>Web ziyaretçileri LAL %1–5 (TR)</li>
                  <li>Video izleyiciler LAL %2 (TR)</li>
                </ul>
              </div>
            </div>

            <p className="text-[10px] text-gray-400 mt-2">
              Erişim odaklı setlerde geniş ilgi alanı + LAL ile ölçek; etkileşim/retargeting setlerinde özel kitleler
              önceliklidir. İlgi alanları ve kitle eşikleri performansa göre kampanya boyunca optimize edilir.
            </p>
          </div>

          {/* Bütçe kırılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Toplam Bütçe" value={fmtCur(TOTAL)} sub="müşteri toplam" />
              <Stat label="AHK (Ajans %10)" value={'-' + fmtCur(AHK_FEE)} sub={`${fmtCur(TOTAL)} × 0.10`} tone="indigo" />
              <Stat label="Lokalizasyon + Planlama" value={'-' + fmtCur(LOCALIZATION)} sub="lokalizasyon + medya planlama" tone="indigo" />
              <Stat label="Stopaj (%15)" value={'-' + fmtCur(STOPAJ)} sub={`${fmtCur(MEDIA_BRUT)} × 0.15`} tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Günlük ortalama net harcama ≈ {fmtCur(dailyNet)} ({DAYS} gün). AHK + Lokalizasyon &amp; Medya Planlama + Stopaj
              toplam {fmtCur(TOTAL)} bütçeye dahildir.
            </p>
          </div>

          {/* İçerik / format dağılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">İçerik &amp; Bütçe Dağılımı (4 İçerik)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">İçerik</th>
                    <th className="text-left px-3 py-2 font-semibold">Boyut</th>
                    <th className="text-left px-3 py-2 font-semibold">Yerleşim</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe (NET)</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim*</th>
                    <th className="text-right px-3 py-2 font-semibold">Etkileşim</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {formats.map((f) => (
                    <tr key={f.name} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-800">{f.name}</td>
                      <td className="px-3 py-2 text-gray-500">{f.size}</td>
                      <td className="px-3 py-2 text-gray-500">{f.placement}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtCur(f.budget)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(f.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(f.reach)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(f.engagement)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{f.cpm.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(estReach)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.engagement)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpm.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Birim maliyetler <strong>Canon Meta hesabının Mayıs–Haziran 2026 gerçekleşen</strong> verisinden alınmıştır:
              CPM ≈ ₺{REAL_CPM.toFixed(2)}, etkileşim oranı ≈ %{(REAL_ENG_RATE * 100).toFixed(1)} (actions/gösterim),
              video ThruPlay maliyeti ≈ ₺{REAL_CPV.toFixed(2)}. Hesap yerleşim kırılımı vermediğinden bu değerler blended uygulanır.
              *Erişim, ~{FREQ} aylık frekans varsayımıyla tahminidir (tekil aylık erişim hesapta tutulmadığından tek açık varsayım).
              Video için tahmini ThruPlay ≈ {fmtInt(thruPlay)}. Sayılar açık artırma dinamiklerine göre değişebilir.
            </p>
          </div>

          {/* Projeksiyon özeti */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Tahmini Erişim" value={fmtInt(estReach)} sub={`~${FREQ} frekans`} tone="green" />
            <Stat label="Toplam Gösterim" value={fmtInt(totals.impressions)} />
            <Stat label="Toplam Etkileşim" value={fmtInt(totals.engagement)} sub={`≈ %${engRate.toFixed(2)} oran`} tone="green" />
            <Stat label="Video ThruPlay" value={fmtInt(thruPlay)} sub="tahmini video izlenme" />
          </div>

          {/* Zamanlama */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-bold text-gray-800">Zamanlama</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Yayın: <strong>1 – 31 Temmuz 2026</strong> (31 gün), ay boyunca kesintisiz. Owned içerikler
              ayın başında yoğunlaşır; video ve carousel ile etkileşim ivmesi korunur. Bütçe gün bazında dengeli
              dağıtılır; performansa göre format/yerleşim ağırlıkları optimize edilir.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              {new Date().toLocaleDateString('tr-TR')} · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
